import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Account } from "src/app/models/account.model";
import { BillingOrder } from "src/app/models/billingorder.model";
import { ApplicationType, ShortCodesRole } from "src/app/models/constants";
import { Lease } from "src/app/models/lease.model";
import { MonthlyInvoice } from "src/app/models/monthlyinvoice.model";
import { Payment } from "src/app/models/payment.model";
import { TableColumn } from "src/app/models/table-columns";
import { ScrBaseComponent } from "../../base.component";
import { flatten } from 'lodash-es';
import { BehaviorSubject, Subscription } from "rxjs";
import { CheckoutState } from "./checkout.payment.component";


@Component({
  selector: 'app-payment-review',
  templateUrl: './review.component.html'
})
export class ReviewComponent extends ScrBaseComponent implements OnInit, OnDestroy {

  constructor() {
    super();
  }
  CognitoGroup: typeof ShortCodesRole = ShortCodesRole;

  account!: Account;

  @Input() checkoutState!: BehaviorSubject<CheckoutState>;

  @Input() monthlyInvoice?: MonthlyInvoice;
  @Input() payment?: Payment;
  @Input() billingOrder?: BillingOrder;

  public tableColumns: TableColumn[] = [];
  public leases!: Lease[];
  public totalPrice = 0;
  public applyCredit = false;
  public creditToBeApplied = 0;
  public amountDueAfterCredit = 0;
  public creditAmount = 0;
  private subscription?: Subscription;

  ngOnInit(): void {
    this.subscription = this.checkoutState.subscribe(upd => {

      // console.trace('review received update ' + ScrBaseService.jsonStringifyRecursive(upd));

      if (upd.account) {
        if (!this.account) {
          this.account = upd.account;
          this.initDisplay();
        }
      }

      let updateDisplay = false;
      if (upd.credit !== undefined) {
        this.creditAmount = upd.credit;
        updateDisplay = true;
      }
      if (upd.applyCredit !== undefined) {
        this.applyCredit = upd.applyCredit;
        updateDisplay = true;
      }

      if (updateDisplay) {
        if (this.applyCredit === true) {
          this.creditToBeApplied = Math.min(this.creditAmount, this.totalPrice);
          this.amountDueAfterCredit = Math.round(100 * (this.totalPrice - this.creditToBeApplied)) / 100;
        }
        else {
          this.creditToBeApplied = 0;
          this.amountDueAfterCredit = this.totalPrice;
        }
        this.checkoutState.next({amountDueAfterCredit: this.amountDueAfterCredit, creditToBeApplied: this.creditToBeApplied});
      }
    });
  }

  public override ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }



  initDisplay() {

    if (this.monthlyInvoice) {
      this.tableColumns = [];
      this.totalPrice = this.monthlyInvoice.totalPrice;
    }
    else if (this.payment && this.payment.orders) {
      this.totalPrice = this.payment.totalPrice ?? 0;

      this.leases = flatten(this.payment.orders?.map(o => o.leases));

      // if you want to test with no discount, do this:
      // this.leases.forEach(l => {l.discountList = undefined; l.discountAmount = undefined;});

      const isDiscounted = this.leases.find(lease => { if (lease.discountAmount && lease.discountAmount > 0) return lease.discountAmount > 0; else return false; });
      if (isDiscounted) {
        this.tableColumns = [
          { field: 'orderId', width: '3' },
          { field: 'scId', width: '8' },
          { field: 'autoRenew', width: '9' },
          { field: 'addToMonthlyBilling', width: '10' },
          { field: 'description', width: '27' },
          { field: 'listPrice', width: '10' },
          { field: 'discount', width: '23' },
          { field: 'price', width: '10' },
        ];
      }
      else {
        this.tableColumns = [
          { field: 'orderId', width: '10' },
          { field: 'scId', width: '12' },
          { field: 'autoRenew', width: '15' },
          { field: 'addToMonthlyBilling', width: '15' },
          { field: 'description', width: '30' },
          { field: 'listPrice', width: '10' },
          { field: 'price', width: '10' },
        ];
      }
      // take out MB if not enabled.
      if (!this.account.monBillEnabled) {
        this.tableColumns = this.tableColumns.filter(tc => tc.field !== 'addToMonthlyBilling');
      }
    }
    else if (this.billingOrder) {
      this.totalPrice = this.billingOrder.totalPrice ?? 0;
      this.leases = this.billingOrder.leases;
      const isDiscounted = this.billingOrder.leases.find(lease => { if (lease.discountAmount && lease.discountAmount > 0) return lease.discountAmount > 0; else return false; });
      if (isDiscounted) {
        this.tableColumns = [
          { field: 'scId', width: '3' },
          { field: 'shortCodeType', width: '3' },
          { field: 'term', width: '3' },
          { field: 'listPrice', width: '4' },
          { field: 'discount', width: '6' },
          { field: 'price', width: '4' },
        ];
      }
      else {
        this.tableColumns = [
          { field: 'scId', width: '3' },
          { field: 'shortCodeType', width: '3' },
          { field: 'term', width: '3' },
          { field: 'listPrice', width: '3' },
          { field: 'price', width: '4' },
        ];
      }
    }
    this.localizeTableColumns(this.tableColumns);

    this.checkoutState.next({
      credit: this.creditAmount,
      totalPrice: this.totalPrice,
      anyAutoRenew: (this.leases ?? []).some(l =>l.autorenew),
      anyMonthlyBilling: (this.leases ?? []).some(l =>l.addToMb && l.applicationType === ApplicationType.New)
    });

  }

  addToMbChecked(lease: Lease): void {
    let anyAutoRenew: boolean | undefined;

    if (lease.addToMb) {
      lease.autorenew = true;
      anyAutoRenew = true;
    }
    const anyMonthlyBilling = this.leases.some(l => l.addToMb && l.applicationType === ApplicationType.New);
    this.checkoutState.next({ anyMonthlyBilling, anyAutoRenew });
  }

  autoRenewChecked(lease: Lease): void {
    if (!lease.autorenew) {
      lease.addToMb = false;
    }
    const anyAutoRenew = this.leases.some(l => l.autorenew);
    const anyMonthlyBilling = this.leases.some(l => l.addToMb && l.applicationType === ApplicationType.New);
    this.checkoutState.next({ anyMonthlyBilling, anyAutoRenew });
  }

  getDiscountList(lease: Lease) {
    if (!lease.discountList || lease.discountList.length === 0) {
      return '';
    }
    else {
      return lease.discountList.map(disc => disc.displayName).join(', ');
    }
  }

}